/**
 * Trade distribution donuts (symbol, long/short, win/loss, session) — shadcn donut + text.
 */

import React, { useMemo } from "react";
import { Box } from "@chakra-ui/react";
import { useTheme } from "next-themes";
import type { ChartConfig } from "@/components/ui/chart";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { TradeStatisticsFilters, DistributionResponse } from "../../../../types/trade";
import { useDistribution, useSessionPerformance } from "../../../../hooks/useTradeStatistics";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";
import { formatSessionDisplayName } from "../../../../utils/sessionDisplayName";
import {
  DistributionPieChartSkeleton,
  DistributionPieLabelChart,
  type DistributionDonutCenter,
  type DistributionPieDatum,
} from "./DistributionPieLabelChart";
import { DashboardWidgetLoadingCard } from "../../Dashboard/components/DashboardWidgetLoadingCard";
import {
  STATS_CHART_TOOLTIP_LABEL_CLASS,
  STATS_CHART_TOOLTIP_TITLE_CLASS,
  STATS_CHART_TOOLTIP_VALUE_CLASS,
} from "./statisticsChartTooltip";

export type TradeDistributionPanel =
  | "all"
  | "bySymbol"
  | "longShort"
  | "winLoss"
  | "bySession"
  | "tradeDistribution";

const SESSION_DISTRIBUTION_TOP_N = 5;
const DISTRIBUTION_OTHER_LABEL = "Other";

/** Glacial green (#06E363) / neon red — same as Daily Trading Activity. */
const DISTRIBUTION_POSITIVE_COLOR = "#06E363";
const DISTRIBUTION_NEGATIVE_COLOR = "#F70009";

/** Tessera brand blues — @see src/Branding/brand-guidelines.md */
const BRAND_TROPICAL_SURF = "#38BBD1";
const BRAND_SKY_GLACIER = "#73E6F0";
const BRAND_NEON_AQUA = "#3EEAEC";
const BRAND_MINT_BREEZE = "#96FBF7";
const BRAND_SOFT_GLACIAL_GREY = "#D3D3D3";

const BRAND_BLUE_PALETTE = [
  BRAND_TROPICAL_SURF,
  BRAND_SKY_GLACIER,
  BRAND_NEON_AQUA,
  BRAND_MINT_BREEZE,
] as const;

function toSegmentKey(label: string): string {
  const key = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  return key || "slice";
}

const SESSION_SLICE_COLORS: Record<string, string> = {
  asian: BRAND_TROPICAL_SURF,
  tokyo: BRAND_TROPICAL_SURF,
  sydney: BRAND_SKY_GLACIER,
  european: BRAND_SKY_GLACIER,
  london: BRAND_NEON_AQUA,
  us: BRAND_NEON_AQUA,
  new_york: BRAND_NEON_AQUA,
  ny: BRAND_NEON_AQUA,
  asian_european: BRAND_MINT_BREEZE,
  european_us: BRAND_SKY_GLACIER,
  asian_european_us: BRAND_MINT_BREEZE,
  unknown: BRAND_SOFT_GLACIAL_GREY,
};

function sessionSliceColor(sessionId: string, index: number): string {
  const key = sessionId.toLowerCase();
  if (key in SESSION_SLICE_COLORS) {
    return SESSION_SLICE_COLORS[key];
  }
  return BRAND_BLUE_PALETTE[index % BRAND_BLUE_PALETTE.length];
}

function symbolSliceColor(symbol: string, index: number): string {
  if (symbol === DISTRIBUTION_OTHER_LABEL) {
    return BRAND_SOFT_GLACIAL_GREY;
  }
  return BRAND_BLUE_PALETTE[index % BRAND_BLUE_PALETTE.length];
}

type PieSeriesEntry = {
  label: string;
  value: number;
  color: string;
};

function buildPieSeries(
  entries: PieSeriesEntry[]
): { data: DistributionPieDatum[]; config: ChartConfig } {
  const config: ChartConfig = {};
  const data: DistributionPieDatum[] = entries
    .filter((e) => e.value > 0)
    .map((entry) => {
      const segment = toSegmentKey(entry.label);
      config[segment] = { label: entry.label, color: entry.color };
      return {
        segment,
        label: entry.label,
        trades: entry.value,
        fill: entry.color,
      };
    });
  return { data, config };
}

function formatDominantPercent(pct: number): string {
  return pct >= 10 ? pct.toFixed(0) : pct.toFixed(1);
}

function longShortDonutCenter(long: number, short: number): DistributionDonutCenter | undefined {
  const total = long + short;
  if (total <= 0) return undefined;
  const longPct = (long / total) * 100;
  const shortPct = (short / total) * 100;
  if (longPct >= shortPct) {
    return {
      title: "Long",
      value: `${formatDominantPercent(longPct)}%`,
    };
  }
  return {
    title: "Short",
    value: `${formatDominantPercent(shortPct)}%`,
  };
}

/** Largest slice by count; % is share of all trades (use totalTrades when pie groups into Other). */
function dominantShareDonutCenter(
  entries: Array<{ label: string; value: number }>,
  totalTrades?: number
): DistributionDonutCenter | undefined {
  if (entries.length === 0) return undefined;
  const denominator =
    totalTrades ?? entries.reduce((sum, e) => sum + e.value, 0);
  if (denominator <= 0) return undefined;
  let best = entries[0];
  for (const entry of entries) {
    if (entry.value > best.value) {
      best = entry;
    }
  }
  const pct = (best.value / denominator) * 100;
  return {
    title: best.label,
    value: `${pct.toFixed(1)}%`,
  };
}

function winLossDonutCenter(
  wins: number,
  losses: number,
  scratch: number
): DistributionDonutCenter | undefined {
  const total = wins + losses + scratch;
  if (total <= 0) return undefined;
  const winPct = (wins / total) * 100;
  const lossPct = (losses / total) * 100;
  if (winPct >= lossPct) {
    return {
      title: "Win",
      value: `${formatDominantPercent(winPct)}%`,
    };
  }
  return {
    title: "Loss",
    value: `${formatDominantPercent(lossPct)}%`,
  };
}

interface TradeDistributionChartsProps {
  filters?: TradeStatisticsFilters;
  dataOverride?: DistributionResponse | null;
  panel?: TradeDistributionPanel;
  /** When false, skip session-performance fetch (e.g. dashboard tradeDistribution panel). */
  includeSession?: boolean;
}

const DISTRIBUTION_PANEL_COUNT: Record<TradeDistributionPanel, number> = {
  all: 4,
  bySymbol: 1,
  longShort: 1,
  winLoss: 1,
  bySession: 1,
  tradeDistribution: 3,
};

const gridSx = (panel: TradeDistributionPanel) => {
  const count = DISTRIBUTION_PANEL_COUNT[panel];
  if (panel === "tradeDistribution") {
    return {
      display: "grid",
      width: "100%",
      gap: "24px",
      gridTemplateColumns: {
        base: "minmax(0,1fr)",
        md: "repeat(2, minmax(0,1fr))",
        lg: "repeat(3, minmax(0,1fr))",
      },
    };
  }
  if (count === 1) {
    return {
      display: "grid",
      width: "100%",
      gap: "24px",
      gridTemplateColumns: "minmax(0,1fr)",
    };
  }
  return {
    display: "grid",
    width: "100%",
    gap: "24px",
    gridTemplateColumns: {
      base: "minmax(0,1fr)",
      md: "repeat(2, minmax(0,1fr))",
      lg: "repeat(4, minmax(0,1fr))",
    },
  };
};

const TradeDistributionCharts: React.FC<TradeDistributionChartsProps> = ({
  filters,
  dataOverride,
  panel = "all",
  includeSession = true,
}) => {
  const { resolvedTheme } = useTheme();

  const { data, loading, error, errorAction } = useDistribution(filters, {
    enabled: dataOverride === undefined,
  });
  const showSessionPanel = includeSession && (panel === "all" || panel === "bySession");
  const {
    data: sessionData,
    loading: sessionLoading,
    error: sessionError,
    errorAction: sessionErrorAction,
  } = useSessionPerformance(filters, { enabled: showSessionPanel });
  const effectiveData = dataOverride ?? data;
  const effectiveLoading = dataOverride ? false : loading;
  const effectiveError = dataOverride ? null : error;
  const effectiveErrorAction = dataOverride ? null : errorAction;
  const sessionLoadingEffective = showSessionPanel && sessionLoading;
  const sessionErrorEffective = showSessionPanel ? sessionError : null;

  const symbolDistribution = useMemo(() => {
    if (!effectiveData?.by_symbol?.length) {
      return {
        labels: [] as string[],
        counts: [] as number[],
        totalTrades: 0,
        symbolCount: 0,
      };
    }

    const rows = effectiveData.by_symbol;
    const symbolCount = rows.length;
    const totalTrades = rows.reduce((sum, s) => sum + (s.count || 0), 0);

    const labels = rows.map((s) => s.symbol);
    const counts = rows.map((s) => s.count);

    return {
      labels,
      counts,
      totalTrades,
      symbolCount,
    };
  }, [effectiveData]);

  const symbolPie = useMemo(() => {
    const entries = symbolDistribution.labels.map((label, i) => ({
      label,
      value: symbolDistribution.counts[i] ?? 0,
      color: symbolSliceColor(label, i),
    }));
    return buildPieSeries(entries);
  }, [symbolDistribution]);

  const longShortData = useMemo(() => {
    if (!effectiveData?.long_short) {
      return { long: 0, short: 0, total: 0 };
    }
    const long = effectiveData.long_short.long_trades || 0;
    const short = effectiveData.long_short.short_trades || 0;
    return { long, short, total: long + short };
  }, [effectiveData]);

  const longShortPie = useMemo(
    () =>
      buildPieSeries([
        { label: "Long", value: longShortData.long, color: DISTRIBUTION_POSITIVE_COLOR },
        { label: "Short", value: longShortData.short, color: DISTRIBUTION_NEGATIVE_COLOR },
      ]),
    [longShortData.long, longShortData.short]
  );

  const winLossData = useMemo(() => {
    if (!effectiveData?.win_loss) {
      return { wins: 0, losses: 0, scratch: 0, total: 0 };
    }
    const wins = effectiveData.win_loss.winning_trades || 0;
    const losses = effectiveData.win_loss.losing_trades || 0;
    const scratch = effectiveData.win_loss.scratch_trades || 0;
    return { wins, losses, scratch, total: wins + losses + scratch };
  }, [effectiveData]);

  const winLossPie = useMemo(
    () =>
      buildPieSeries([
        { label: "Win", value: winLossData.wins, color: DISTRIBUTION_POSITIVE_COLOR },
        { label: "Scratch", value: winLossData.scratch, color: BRAND_SOFT_GLACIAL_GREY },
        { label: "Loss", value: winLossData.losses, color: DISTRIBUTION_NEGATIVE_COLOR },
      ]),
    [winLossData.wins, winLossData.losses, winLossData.scratch]
  );

  const sessionDistribution = useMemo(() => {
    const rows =
      sessionData?.sessions
        ?.filter((s) => (s.trade_count || 0) > 0)
        .sort((a, b) => (b.trade_count || 0) - (a.trade_count || 0)) ?? [];

    if (rows.length === 0) {
      return {
        slices: [] as Array<{
          sessionId: string;
          label: string;
          count: number;
        }>,
        totalTrades: 0,
        sessionCount: 0,
      };
    }

    const totalTrades = rows.reduce((sum, s) => sum + (s.trade_count || 0), 0);

    const slices = rows.map((s) => ({
      sessionId: s.session_id,
      label: formatSessionDisplayName(s.session_name),
      count: s.trade_count || 0,
    }));

    return {
      slices,
      totalTrades,
      sessionCount: rows.length,
    };
  }, [sessionData]);

  const sessionPie = useMemo(() => {
    const entries = sessionDistribution.slices.map((slice, i) => ({
      label: slice.label,
      value: slice.count,
      color:
        slice.label === DISTRIBUTION_OTHER_LABEL
          ? BRAND_SOFT_GLACIAL_GREY
          : sessionSliceColor(slice.sessionId, i),
    }));
    return buildPieSeries(entries);
  }, [sessionDistribution.slices]);

  const sessionDonutCenter = useMemo(() => {
    const dominantEntries = sessionDistribution.slices.map((s) => ({
      label: s.label,
      value: s.count,
    }));
    return dominantShareDonutCenter(
      dominantEntries,
      sessionDistribution.totalTrades
    );
  }, [sessionDistribution.slices, sessionDistribution.totalTrades]);

  const symbolDonutCenter = useMemo((): DistributionDonutCenter | undefined => {
    if (symbolDistribution.symbolCount <= 0) return undefined;
    return {
      title: "Symbols traded",
      value: String(symbolDistribution.symbolCount),
    };
  }, [symbolDistribution.symbolCount]);

  const longShortDonutCenterValue = useMemo(
    () => longShortDonutCenter(longShortData.long, longShortData.short),
    [longShortData.long, longShortData.short]
  );

  const winLossDonutCenterValue = useMemo(
    () => winLossDonutCenter(winLossData.wins, winLossData.losses, winLossData.scratch),
    [winLossData.wins, winLossData.losses, winLossData.scratch]
  );

  const showSymbol = panel === "all" || panel === "bySymbol" || panel === "tradeDistribution";
  const showLongShort = panel === "all" || panel === "longShort" || panel === "tradeDistribution";
  const showWinLoss = panel === "all" || panel === "winLoss" || panel === "tradeDistribution";
  const needsDistribution = showSymbol || showLongShort || showWinLoss;

  const themeClass = resolvedTheme === "dark" ? "dark" : "";

  const distributionSkeletonCount = DISTRIBUTION_PANEL_COUNT[panel];

  // Symbol distribution now shows all symbols directly in the pie; no separate "Other" bucket.

  // Session distribution now shows all sessions directly in the pie; no separate "Other" bucket.

  const chartsLoading =
    (needsDistribution && effectiveLoading) || sessionLoadingEffective;

  if (chartsLoading) {
    if (panel === "tradeDistribution") {
      return (
        <DashboardWidgetLoadingCard
          cardType="tradeDistribution"
          variant="tall"
          className="w-full"
        />
      );
    }
    return (
      <Box sx={gridSx(panel)} className={themeClass} w="100%" m={0} p={0}>
        {Array.from({ length: distributionSkeletonCount }, (_, i) => (
          <DistributionPieChartSkeleton key={i} />
        ))}
      </Box>
    );
  }

  const chartsError =
    (needsDistribution && effectiveError) ||
    (showSessionPanel && sessionErrorEffective ? sessionErrorEffective : null);
  const chartsErrorAction =
    needsDistribution && effectiveError
      ? effectiveErrorAction
      : showSessionPanel && sessionErrorEffective
        ? sessionErrorAction
        : null;
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(
    chartsError,
    chartsErrorAction
  );

  if (showWidgetError && widgetError) {
    return (
      <div className={cn(themeClass)}>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (chartsError) {
    return null;
  }

  return (
    <Box sx={gridSx(panel)} className={themeClass} w="100%" m={0} p={0}>
      {showSymbol && (
        <DistributionPieLabelChart
          title="Distribution by Symbol"
          data={symbolPie.data}
          config={symbolPie.config}
          center={symbolDonutCenter}
        />
      )}
      {showLongShort && (
        <DistributionPieLabelChart
          title="Long vs Short"
          data={longShortPie.data}
          config={longShortPie.config}
          center={longShortDonutCenterValue}
        />
      )}
      {showWinLoss && (
        <DistributionPieLabelChart
          title="Win vs Loss"
          data={winLossPie.data}
          config={winLossPie.config}
          center={winLossDonutCenterValue}
        />
      )}
      {showSessionPanel && (
        <DistributionPieLabelChart
          title="Distribution by Session"
          data={sessionPie.data}
          config={sessionPie.config}
          center={sessionDonutCenter}
        />
      )}
    </Box>
  );
};

export default TradeDistributionCharts;
